import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toIsoDate, syncDateRange } from '../utils/dateUtils';

const emptyForm = {
  rate_plan_code: '',
  rate_plan_name: '',
  meal_plan: 'EP',
  tax_inclusive: false,
  description: '',
  status: 'ACTIVE',
  is_active: true
};

const OCCUPANCY_TYPES = ['SINGLE', 'DOUBLE', 'TRIPLE', 'QUAD', 'EXTRA_ADULT', 'EXTRA_CHILD'];
const OCCUPANCY_LABEL = {
  SINGLE: 'Single',
  DOUBLE: 'Double',
  TRIPLE: 'Triple',
  QUAD: 'Quad',
  EXTRA_ADULT: 'Extra Adult',
  EXTRA_CHILD: 'Extra Child'
};
const MEAL_PLANS = ['EP', 'CP', 'MAP', 'AP'];
const weekdayLabels = {
  EP: 'Room Charges only',
  CP: 'Breakfast included',
  MAP: 'Breakfast and Lunch or Dinner',
  AP: 'Breakfast, Lunch and Dinner'
};

const parseMoneyInput = (v) => {
  const n = Number(String(v || '').trim());
  if (!Number.isFinite(n) || n < 0) return 0;
  return Number(n.toFixed(2));
};
const eachDate = (startDate, endDate, cb) => {
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  for (let cursor = new Date(start); cursor <= end; cursor.setDate(cursor.getDate() + 1)) {
    cb(toIsoDate(cursor));
  }
};
const emptySingleApply = {
  rate_plan_code: '',
  rate_plan_name: '',
  tax_id: '',
  room_type_id: '',
  meal_plan: 'EP',
  stay_start_date: '',
  stay_end_date: '',
  booking_start_date: '',
  booking_end_date: '',
  cutoff_unit: 'DAYS',
  cutoff_value: '0',
  include_adult: 2,
  include_child: 0,
  free_age_limit: 0,
  max_child_age: 0,
  single: 0,
  double: 0,
  triple: 0,
  quad: 0,
  extra_adult: 0,
  extra_child: 0,
  min_los: 1,
  max_los: 0,
  tax_mode: 'EXCLUDED',
  margin_value: 0,
  cancellation_policy_id: ''
};

function PmsRatePlanMaster({ token, selectedProperty }) {
  const [rows, setRows] = useState([]);
  const [taxRows, setTaxRows] = useState([]);
  const [roomTaxPolicyConfigured, setRoomTaxPolicyConfigured] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [roomTypes, setRoomTypes] = useState([]);
  const [cancellationPolicies, setCancellationPolicies] = useState([]);
  const [mealPlans, setMealPlans] = useState([]); // REQ-05: sourced from server meal-plan master
  const [singleApply, setSingleApply] = useState(emptySingleApply);
  const [singleApplyBusy, setSingleApplyBusy] = useState(false);
  // Bug 8686 — scroll to + highlight the form when Edit is clicked from the list.
  const formRef = useRef(null);
  const [editHighlight, setEditHighlight] = useState(false);

  const headers = () => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || ''
  });

  const loadRows = async () => {
    if (!selectedProperty?.id) return setRows([]);
    try {
      const res = await fetch('/api/v1/pms/rate-plans?all=1', { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load rate plans');
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || 'Failed to load rate plans');
    }
  };

  const loadRoomTypes = async () => {
    if (!selectedProperty?.id) return setRoomTypes([]);
    try {
      const res = await fetch('/api/v1/pms/room-types?all=1', { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load room types');
      setRoomTypes((Array.isArray(data) ? data : []).filter((r) => r.is_active !== false));
    } catch (err) {
      setError(err?.message || 'Failed to load room types');
    }
  };

  const loadCancellationPolicies = async () => {
    if (!selectedProperty?.id) return setCancellationPolicies([]);
    try {
      const res = await fetch('/api/v1/pms-policies/', { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load cancellation policies');
      setCancellationPolicies(Array.isArray(data) ? data : []);
    } catch (err) {
      // Non-fatal — rate plans still work without a policy mapped.
      setCancellationPolicies([]);
    }
  };

  const loadTaxRows = async () => {
    if (!selectedProperty?.id) return setTaxRows([]);
    try {
      const res = await fetch('/api/tax-master', { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load tax profiles');
      setTaxRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || 'Failed to load tax profiles');
    }
  };
  const loadMealPlans = async () => {
    if (!selectedProperty?.id) return setMealPlans([]);
    try {
      const res = await fetch('/api/v1/pms/meal-plans', { headers: headers() });
      const data = await res.json();
      if (res.ok && Array.isArray(data)) setMealPlans(data);
    } catch (err) {
      // Non-fatal — dropdown falls back to the built-in EP/CP/MAP/AP list.
    }
  };
  const loadRoomTaxPolicyStatus = async () => {
    if (!selectedProperty?.id) {
      setRoomTaxPolicyConfigured(false);
      return;
    }
    try {
      const statusRes = await fetch('/api/v1/pms/tax-source-status', { headers: headers() });
      const statusData = await statusRes.json().catch(() => ({}));
      if (statusRes.ok) {
        setRoomTaxPolicyConfigured(statusData?.room_policy_configured === true);
        return;
      }
      const res = await fetch('/api/room-tax-policy', { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load room tax policy');
      const hasActiveRows = (Array.isArray(data) ? data : []).some((row) => row?.is_active !== false);
      setRoomTaxPolicyConfigured(hasActiveRows);
    } catch (err) {
      setRoomTaxPolicyConfigured(false);
      setError(err?.message || 'Failed to load room tax policy');
    }
  };

  useEffect(() => {
    setError('');
    setMessage('');
    loadRows();
    loadRoomTypes();
    loadTaxRows();
    loadCancellationPolicies();
    loadMealPlans();
    loadRoomTaxPolicyStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedProperty?.id]);

  const save = async () => {
    if (!selectedProperty?.id) return setError('Select property first');
    if (!String(form.rate_plan_code || '').trim()) return setError('Rate Plan Code is required');
    if (!String(form.rate_plan_name || '').trim()) return setError('Rate Plan Name is required');
    setError('');
    setMessage('');
    try {
      const url = editingId ? `/api/v1/pms/rate-plans/${editingId}` : '/api/v1/pms/rate-plans';
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: headers(),
        body: JSON.stringify({
          ...form,
          rate_plan_code: String(form.rate_plan_code || '').trim().toUpperCase(),
          rate_plan_name: String(form.rate_plan_name || '').trim()
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Save failed');
      setMessage(editingId ? 'Rate plan updated' : 'Rate plan created');
      setForm(emptyForm);
      setEditingId('');
      await loadRows();
    } catch (err) {
      setError(err?.message || 'Save failed');
    }
  };

  const editRow = (row) => {
    setEditingId(String(row.rate_plan_id));
    setForm({
      rate_plan_code: row.rate_plan_code || '',
      rate_plan_name: row.rate_plan_name || '',
      meal_plan: row.meal_plan || 'EP',
      tax_inclusive: row.tax_inclusive === true,
      description: row.description || '',
      status: row.status || 'ACTIVE',
      is_active: row.is_active !== false
    });
    setError('');
    setMessage('');
  };

  const deactivate = async (id) => {
    if (!window.confirm('Deactivate this rate plan?')) return;
    setError('');
    setMessage('');
    try {
      const res = await fetch(`/api/v1/pms/rate-plans/${id}`, { method: 'DELETE', headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Deactivate failed');
      setMessage('Rate plan deactivated');
      loadRows();
    } catch (err) {
      setError(err?.message || 'Deactivate failed');
    }
  };

  const activate = async (id) => {
    setError('');
    setMessage('');
    try {
      const res = await fetch(`/api/v1/pms/rate-plans/${id}/activate`, { method: 'POST', headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Activate failed');
      setMessage('Rate plan activated');
      loadRows();
    } catch (err) {
      setError(err?.message || 'Activate failed');
    }
  };

  const saveSingleApply = async () => {
    if (!selectedProperty?.id) return setError('Select property first');
    const roomTypeId = Number(singleApply.room_type_id);
    const ratePlanCode = String(singleApply.rate_plan_code || '').trim().toUpperCase();
    const ratePlanName = String(singleApply.rate_plan_name || '').trim();
    const taxId = Number(singleApply.tax_id || 0);
    if (!ratePlanCode) return setError('Rate Plan Code is required');
    if (!ratePlanName) return setError('Rate Plan Name is required');
    if (!roomTaxPolicyConfigured && (!Number.isInteger(taxId) || taxId <= 0)) return setError('Tax Profile is required');
    if (!Number.isInteger(roomTypeId) || roomTypeId <= 0) return setError('Room Type is required');
    if (!singleApply.stay_start_date || !singleApply.stay_end_date) return setError('Stay Period is required');
    if (singleApply.stay_start_date > singleApply.stay_end_date) return setError('Stay Start Date cannot be after Stay End Date');
    const minLos = Number(singleApply.min_los ?? 1);
    const maxLos = Number(singleApply.max_los ?? 0);
    if (!Number.isFinite(minLos) || minLos < 1) return setError('Min LOS must be at least 1');
    if (!Number.isFinite(maxLos) || maxLos < 0) return setError('Max LOS must be 0 (no limit) or a positive number');
    if (maxLos > 0 && minLos > maxLos) return setError('Min LOS cannot be greater than Max LOS');

    const roomType = roomTypes.find((rt) => String(rt.room_type_id) === String(roomTypeId));
    if (!roomType) return setError('Selected room type not found');
    const selectedPlan = rows.find((r) => String(r.rate_plan_code || '').toUpperCase() === ratePlanCode) || null;

    const occupancyAmounts = {
      SINGLE: parseMoneyInput(singleApply.single),
      DOUBLE: parseMoneyInput(singleApply.double),
      TRIPLE: parseMoneyInput(singleApply.triple),
      QUAD: parseMoneyInput(singleApply.quad),
      EXTRA_ADULT: parseMoneyInput(singleApply.extra_adult),
      EXTRA_CHILD: parseMoneyInput(singleApply.extra_child)
    };
    // Date expansion is done server-side; frontend sends compact amounts only.

    setError('');
    setMessage('');
    setSingleApplyBusy(true);
    try {
      let ratePlanId = selectedPlan?.rate_plan_id ? Number(selectedPlan.rate_plan_id) : 0;
      if (selectedPlan) {
        const updatePlanRes = await fetch(`/api/v1/pms/rate-plans/${encodeURIComponent(ratePlanId)}`, {
          method: 'PUT',
          headers: headers(),
          body: JSON.stringify({
            rate_plan_code: ratePlanCode,
            rate_plan_name: ratePlanName,
            room_type_id: roomTypeId,
            meal_plan: String(singleApply.meal_plan || selectedPlan.meal_plan || 'EP').toUpperCase(),
            tax_id: roomTaxPolicyConfigured ? null : (singleApply.tax_mode === 'NONE' ? null : taxId),
            is_tax_exempt: singleApply.tax_mode === 'NONE',
            stay_start_date: singleApply.stay_start_date || null,
            stay_end_date: singleApply.stay_end_date || null,
            booking_start_date: singleApply.booking_start_date || null,
            booking_end_date: singleApply.booking_end_date || null,
            rate_single: Number(singleApply.single || 0),
            rate_double: Number(singleApply.double || 0),
            rate_triple: Number(singleApply.triple || 0),
            rate_quad: Number(singleApply.quad || 0),
            rate_extra_adult: Number(singleApply.extra_adult || 0),
            rate_extra_child: Number(singleApply.extra_child || 0),
            min_los: minLos,
            max_los: maxLos,
            tax_inclusive: singleApply.tax_mode === 'INCLUDED',
            cancellation_policy_id: singleApply.cancellation_policy_id || null,
            description: selectedPlan.description || '',
            status: 'ACTIVE',
            // Re-saving rates implies the staff want this plan usable — always
            // re-activate, even if it was previously deactivated.
            is_active: true
          })
        });
        const updatePlanData = await updatePlanRes.json().catch(() => ({}));
        if (!updatePlanRes.ok) throw new Error(updatePlanData?.error || 'Failed to update rate plan name');
      } else {
        const createPlanRes = await fetch('/api/v1/pms/rate-plans', {
          method: 'POST',
          headers: headers(),
          body: JSON.stringify({
            rate_plan_code: ratePlanCode,
            rate_plan_name: ratePlanName,
            room_type_id: roomTypeId,
            meal_plan: String(singleApply.meal_plan || 'EP').toUpperCase(),
            tax_id: roomTaxPolicyConfigured ? null : (singleApply.tax_mode === 'NONE' ? null : taxId),
            is_tax_exempt: singleApply.tax_mode === 'NONE',
            stay_start_date: singleApply.stay_start_date || null,
            stay_end_date: singleApply.stay_end_date || null,
            booking_start_date: singleApply.booking_start_date || null,
            booking_end_date: singleApply.booking_end_date || null,
            rate_single: Number(singleApply.single || 0),
            rate_double: Number(singleApply.double || 0),
            rate_triple: Number(singleApply.triple || 0),
            rate_quad: Number(singleApply.quad || 0),
            rate_extra_adult: Number(singleApply.extra_adult || 0),
            rate_extra_child: Number(singleApply.extra_child || 0),
            min_los: minLos,
            max_los: maxLos,
            tax_inclusive: singleApply.tax_mode === 'INCLUDED',
            cancellation_policy_id: singleApply.cancellation_policy_id || null,
            description: '',
            status: 'ACTIVE',
            is_active: true
          })
        });
        const createPlanData = await createPlanRes.json().catch(() => ({}));
        if (!createPlanRes.ok) throw new Error(createPlanData?.error || 'Failed to create rate plan');
        ratePlanId = Number(createPlanData?.rate_plan_id || 0);
      }
      if (!ratePlanId) throw new Error('Unable to resolve rate plan id');

      const res = await fetch(`/api/v1/pms/rate-plans/${encodeURIComponent(ratePlanId)}/calendar-rates-range`, {
        method: 'PUT',
        headers: headers(),
        body: JSON.stringify({ meal_plan: singleApply.meal_plan, amounts: occupancyAmounts, min_los: minLos, max_los: maxLos })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to save single apply rates');
      await loadRows();
      const daysSaved = Number(data?.days_saved || 0);
      setMessage(daysSaved > 0
        ? `Rate plan saved successfully for ${daysSaved} day${daysSaved !== 1 ? 's' : ''}.`
        : `Rate plan saved successfully.`
      );
    } catch (err) {
      setError(err?.message || 'Failed to save single apply rates');
    } finally {
      setSingleApplyBusy(false);
    }
  };

  const editRatePlanFromList = (row) => {
    setSingleApply((prev) => ({
      ...prev,
      rate_plan_code: String(row.rate_plan_code || ''),
      rate_plan_name: String(row.rate_plan_name || ''),
      room_type_id: row.room_type_id ? String(row.room_type_id) : '',
      meal_plan: String(row.meal_plan || 'EP').toUpperCase(),
      tax_id: row.tax_id ? String(row.tax_id) : '',
      stay_start_date: row.stay_start_date ? String(row.stay_start_date).slice(0, 10) : '',
      stay_end_date: row.stay_end_date ? String(row.stay_end_date).slice(0, 10) : '',
      booking_start_date: row.booking_start_date ? String(row.booking_start_date).slice(0, 10) : '',
      booking_end_date: row.booking_end_date ? String(row.booking_end_date).slice(0, 10) : '',
      single: Number(row.rate_single || 0),
      double: Number(row.rate_double || 0),
      triple: Number(row.rate_triple || 0),
      quad: Number(row.rate_quad || 0),
      extra_adult: Number(row.rate_extra_adult || 0),
      extra_child: Number(row.rate_extra_child || 0),
      min_los: Number(row.min_los ?? 1),
      max_los: Number(row.max_los ?? 0),
      tax_mode: row.is_tax_exempt === true ? 'NONE' : (row.tax_inclusive === true ? 'INCLUDED' : 'EXCLUDED'),
      cancellation_policy_id: row.cancellation_policy_id ? String(row.cancellation_policy_id) : ''
    }));
    setMessage(`Editing: ${row.rate_plan_code} - ${row.rate_plan_name}`);
    setError('');
    // Bring the form into view and briefly highlight it so the edit is obvious.
    setEditHighlight(true);
    setTimeout(() => { formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 60);
    setTimeout(() => setEditHighlight(false), 2000);
  };

  return (
    <div style={{ padding: '1rem' }}>
      <h3 style={{ marginTop: 0, color: 'var(--theme-primary)' }}>Front Office - Rate Plan Master</h3>
      {error ? <div className="error-banner">{error}</div> : null}
      {message ? <div className="success-banner">{message}</div> : null}

      <div ref={formRef} style={{ border: '1px solid #d7e3f4', borderRadius: '10px', padding: '0.85rem', marginBottom: '1rem', background: 'linear-gradient(180deg,#f8fbff 0%,#eef6ff 100%)', transition: 'box-shadow 0.3s ease, border-color 0.3s ease', borderColor: editHighlight ? 'var(--theme-primary)' : '#d7e3f4', boxShadow: editHighlight ? '0 0 0 4px rgba(37, 99, 235, 0.15)' : 'none' }}>
        <div style={{ fontWeight: 700, color: 'var(--theme-primary)', marginBottom: '0.65rem' }}>Rate Plan Creation - Single View Apply{editHighlight && <span style={{ marginLeft: 10, fontSize: '0.8rem', fontWeight: 600 }}>✎ Editing below</span>}</div>
        {roomTaxPolicyConfigured ? (
          <div style={{ marginBottom: '0.55rem', fontSize: '0.82rem', color: '#2c5282' }}>
            Tax source: Hotel Room Tax Policy (rate plan tax profile not required).
          </div>
        ) : null}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: '0.6rem' }}>
          <label style={{ display: 'grid', gap: '0.25rem' }}>
            <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Rate Plan Code *</span>
            <input value={singleApply.rate_plan_code} onChange={(e) => setSingleApply({ ...singleApply, rate_plan_code: e.target.value.toUpperCase() })} placeholder="RACK / CORP / BAR" />
          </label>
          <label style={{ display: 'grid', gap: '0.25rem' }}>
            <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Rate Plan Name *</span>
            <input value={singleApply.rate_plan_name} onChange={(e) => setSingleApply({ ...singleApply, rate_plan_name: e.target.value })} placeholder="Rack Rate / Corporate Rate" />
          </label>
          {!roomTaxPolicyConfigured ? (
            <label style={{ display: 'grid', gap: '0.25rem' }}>
              <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Tax Profile *</span>
              <select value={singleApply.tax_id} onChange={(e) => setSingleApply({ ...singleApply, tax_id: e.target.value })}>
                <option value="">-- Select Tax Profile --</option>
                {taxRows.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.tax_name} [{String(row.tax_regime || 'NONE').toUpperCase()}] ({Number(row.tax_percent || 0).toFixed(2)}%)
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <label style={{ display: 'grid', gap: '0.25rem' }}>
            <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Room Type *</span>
            <select value={singleApply.room_type_id} onChange={(e) => setSingleApply({ ...singleApply, room_type_id: e.target.value })}>
              <option value="">-- Select Room Type --</option>
              {roomTypes.map((rt) => <option key={rt.room_type_id} value={rt.room_type_id}>{rt.room_type_name}</option>)}
            </select>
          </label>
          <label style={{ display: 'grid', gap: '0.25rem' }}>
            <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Meal Plan</span>
            <select value={singleApply.meal_plan} onChange={(e) => setSingleApply({ ...singleApply, meal_plan: e.target.value })}>
              {(mealPlans.length ? mealPlans.filter((m) => m.is_active !== false) : MEAL_PLANS.map((c) => ({ code: c, label: c }))).map((mp) => (
                <option key={mp.code} value={mp.code}>{mp.code}{mp.label && mp.label !== mp.code ? ` — ${mp.label}` : ''}</option>
              ))}
            </select>
            <span style={{ fontSize: '0.72rem', color: '#64748b' }}>
              {(mealPlans.find((m) => m.code === singleApply.meal_plan)?.meal_inclusions) || weekdayLabels[singleApply.meal_plan] || ''}
            </span>
          </label>
          <label style={{ display: 'grid', gap: '0.25rem' }}>
            <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Cancellation Policy</span>
            <select value={singleApply.cancellation_policy_id} onChange={(e) => setSingleApply({ ...singleApply, cancellation_policy_id: e.target.value })}>
              <option value="">-- None (Free Cancellation) --</option>
              {cancellationPolicies.map((p) => (
                <option key={p.policy_id} value={p.policy_id}>
                  {p.policy_name}{p.window_hours != null ? ` (${p.window_hours}h)` : ''}
                </option>
              ))}
            </select>
          </label>
          <div style={{ alignSelf: 'end', fontSize: '0.8rem', color: '#4a5568' }}>
            {weekdayLabels[singleApply.meal_plan] || ''}
          </div>
        </div>

        <div style={{ marginTop: '0.75rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: '0.6rem' }}>
          <label style={{ display: 'grid', gap: '0.25rem' }}><span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Stay Start *</span><input type="date" value={singleApply.stay_start_date} onChange={(e) => setSingleApply(syncDateRange({ ...singleApply, stay_start_date: e.target.value }, 'stay_start_date', 'stay_end_date', 'stay_start_date'))} /></label>
          <label style={{ display: 'grid', gap: '0.25rem' }}><span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Stay End *</span><input type="date" value={singleApply.stay_end_date} min={singleApply.stay_start_date || undefined} onChange={(e) => setSingleApply(syncDateRange({ ...singleApply, stay_end_date: e.target.value }, 'stay_start_date', 'stay_end_date', 'stay_end_date'))} /></label>
        </div>

        <div style={{ marginTop: '0.75rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(120px,1fr))', gap: '0.5rem' }}>
          <label style={{ display: 'grid', gap: '0.25rem' }}><span style={{ fontSize: '0.8rem', fontWeight: 600 }}>Single</span><input type="number" min="0" step="0.01" value={singleApply.single} onChange={(e) => setSingleApply({ ...singleApply, single: e.target.value })} /></label>
          <label style={{ display: 'grid', gap: '0.25rem' }}><span style={{ fontSize: '0.8rem', fontWeight: 600 }}>Double</span><input type="number" min="0" step="0.01" value={singleApply.double} onChange={(e) => setSingleApply({ ...singleApply, double: e.target.value })} /></label>
          <label style={{ display: 'grid', gap: '0.25rem' }}><span style={{ fontSize: '0.8rem', fontWeight: 600 }}>Triple</span><input type="number" min="0" step="0.01" value={singleApply.triple} onChange={(e) => setSingleApply({ ...singleApply, triple: e.target.value })} /></label>
          <label style={{ display: 'grid', gap: '0.25rem' }}><span style={{ fontSize: '0.8rem', fontWeight: 600 }}>Quad</span><input type="number" min="0" step="0.01" value={singleApply.quad} onChange={(e) => setSingleApply({ ...singleApply, quad: e.target.value })} /></label>
          <label style={{ display: 'grid', gap: '0.25rem' }}><span style={{ fontSize: '0.8rem', fontWeight: 600 }}>Extra Adult</span><input type="number" min="0" step="0.01" value={singleApply.extra_adult} onChange={(e) => setSingleApply({ ...singleApply, extra_adult: e.target.value })} /></label>
          <label style={{ display: 'grid', gap: '0.25rem' }}><span style={{ fontSize: '0.8rem', fontWeight: 600 }}>Extra Child</span><input type="number" min="0" step="0.01" value={singleApply.extra_child} onChange={(e) => setSingleApply({ ...singleApply, extra_child: e.target.value })} /></label>
        </div>

        <div style={{ marginTop: '0.75rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: '0.6rem' }}>
          <label style={{ display: 'grid', gap: '0.25rem' }}><span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Min LOS</span><input type="number" min="1" value={singleApply.min_los} onChange={(e) => setSingleApply({ ...singleApply, min_los: e.target.value })} /></label>
          <label style={{ display: 'grid', gap: '0.25rem' }}><span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Max LOS <span style={{ fontWeight: 400, color: '#6b7280' }}>(0 = no limit)</span></span><input type="number" min="0" value={singleApply.max_los} onChange={(e) => setSingleApply({ ...singleApply, max_los: e.target.value })} /></label>
          <label style={{ display: 'grid', gap: '0.25rem' }}><span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Tax</span><select value={singleApply.tax_mode} onChange={(e) => setSingleApply({ ...singleApply, tax_mode: e.target.value })}><option value="INCLUDED">Included</option><option value="EXCLUDED">Excluded</option><option value="NONE">None (Tax-Exempt)</option></select></label>
          <label style={{ display: 'grid', gap: '0.25rem' }}><span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Margin</span><input type="number" min="0" step="0.01" value={singleApply.margin_value} onChange={(e) => setSingleApply({ ...singleApply, margin_value: e.target.value })} /></label>
          <div style={{ display: 'flex', alignItems: 'end', gap: '0.5rem' }}>
            <button disabled={singleApplyBusy} onClick={saveSingleApply}>{singleApplyBusy ? 'Saving...' : 'Save Single View Rates'}</button>
          </div>
        </div>
      </div>

      <div className="card" style={{ padding: '0.85rem' }}>
        <div className="section-head">Rate Plan List</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #d9e2ec' }}>
            <thead>
              <tr style={{ background: 'var(--theme-primary)', color: '#fff' }}>
                <th style={{ textAlign: 'left', padding: '0.45rem' }}>Code</th>
                <th style={{ textAlign: 'left', padding: '0.45rem' }}>Name</th>
                <th style={{ textAlign: 'left', padding: '0.45rem' }}>Room Type</th>
                <th style={{ textAlign: 'left', padding: '0.45rem' }}>Stay Start</th>
                <th style={{ textAlign: 'left', padding: '0.45rem' }}>Stay End</th>
                <th style={{ textAlign: 'left', padding: '0.45rem' }}>Booking Start</th>
                <th style={{ textAlign: 'left', padding: '0.45rem' }}>Booking End</th>
                <th style={{ textAlign: 'left', padding: '0.45rem' }}>Meal Plan</th>
                <th style={{ textAlign: 'left', padding: '0.45rem' }}>Cancellation Policy</th>
                <th style={{ textAlign: 'left', padding: '0.45rem' }}>Tax Profile</th>
                <th style={{ textAlign: 'right', padding: '0.45rem' }}>Min LOS</th>
                <th style={{ textAlign: 'right', padding: '0.45rem' }}>Max LOS</th>
                <th style={{ textAlign: 'left', padding: '0.45rem' }}>Status</th>
                <th style={{ textAlign: 'left', padding: '0.45rem' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.rate_plan_id} style={{ borderBottom: '1px solid var(--theme-border)' }}>
                  <td style={{ padding: '0.45rem', fontWeight: 700 }}>{row.rate_plan_code}</td>
                  <td style={{ padding: '0.45rem' }}>{row.rate_plan_name}</td>
                  <td style={{ padding: '0.45rem' }}>{row.room_type_name || '-'}</td>
                  <td style={{ padding: '0.45rem' }}>{row.stay_start_date ? String(row.stay_start_date).slice(0, 10) : '-'}</td>
                  <td style={{ padding: '0.45rem' }}>{row.stay_end_date ? String(row.stay_end_date).slice(0, 10) : '-'}</td>
                  <td style={{ padding: '0.45rem' }}>{row.booking_start_date ? String(row.booking_start_date).slice(0, 10) : '-'}</td>
                  <td style={{ padding: '0.45rem' }}>{row.booking_end_date ? String(row.booking_end_date).slice(0, 10) : '-'}</td>
                  <td style={{ padding: '0.45rem' }}>{row.meal_plan || '-'}</td>
                  <td style={{ padding: '0.45rem' }}>{row.cancellation_policy_name || '-'}</td>
                  <td style={{ padding: '0.45rem' }}>
                    {roomTaxPolicyConfigured
                      ? 'Hotel Room Tax Policy'
                      : (row.tax_name ? `${row.tax_name} [${String(row.tax_regime || 'NONE').toUpperCase()}]` : 'NONE')}
                  </td>
                  <td style={{ padding: '0.45rem', textAlign: 'right' }}>{Number(row.min_los ?? 1)}</td>
                  <td style={{ padding: '0.45rem', textAlign: 'right' }}>{Number(row.max_los ?? 0) === 0 ? '∞' : Number(row.max_los)}</td>
                  <td style={{ padding: '0.45rem' }}>{row.is_active ? 'Active' : 'Inactive'}</td>
                  <td style={{ padding: '0.45rem', display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                    <button onClick={() => editRatePlanFromList(row)}>Edit</button>
                    {row.is_active
                      ? <button onClick={() => deactivate(row.rate_plan_id)} className="btn-danger" style={{ border: 'none', borderRadius: '4px', padding: '0.4rem 0.65rem', cursor: 'pointer' }}>Deactivate</button>
                      : <button onClick={() => activate(row.rate_plan_id)} className="btn-success" style={{ border: 'none', borderRadius: '4px', padding: '0.4rem 0.65rem', cursor: 'pointer' }}>Activate</button>}
                  </td>
                </tr>
              ))}
              {!rows.length ? <tr><td colSpan={14} style={{ padding: '0.65rem' }}>No rate plans found</td></tr> : null}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
}

export default PmsRatePlanMaster;
